import { fetchAllCountryRecords } from './country'
import { publishWarningRelease } from './warningRelease'
import { http } from './request'
import { TRAVEL_RISK_TAG } from '@/utils/travelRiskLevel'

export type RiskLevel = 'extreme' | 'high' | 'medium' | 'low'

export interface RiskMapPoint {
  id: string
  country: string
  city: string
  level: RiskLevel
  lng: number
  lat: number
}

export interface RiskLevelStat {
  level: RiskLevel
  label: string
  count: number
  unit: string
}

export interface RiskUpdateRecord {
  id: string
  country: string
  timeText: string
  fromLevel: RiskLevel
  toLevel: RiskLevel
  /** true 表示本次变更前未设置风险等级（fromRiskLevel 为空） */
  fromMissing?: boolean
}

/** 各风险等级下的国家中文名列表（与 levelStats 统计口径一致，已排序） */
export type CountriesByRiskLevel = Record<'extreme' | 'high' | 'medium' | 'low', string[]>

export interface RiskWarningDashboard {
  mapPoints: RiskMapPoint[]
  levelStats: RiskLevelStat[]
  updates: RiskUpdateRecord[]
  countriesByLevel: CountriesByRiskLevel
}

export interface PublishRiskPayload {
  id: string
}

interface CountryRiskChangeRecentItem {
  geonameid: number
  countryCode?: string
  countryName?: string
  fromRiskLevel?: string | null
  toRiskLevel: string
  operateTime?: string | Date
  operatorName?: string
}

interface SpringPage<T> {
  records?: T[]
  total?: number
}

const LEVEL_LABEL: Record<RiskLevel, string> = TRAVEL_RISK_TAG

function normalizeRiskLevel(v: unknown): RiskLevel {
  const s = String(v ?? '').toLowerCase()
  if (s === 'extreme') return 'extreme'
  if (s === 'high' || s === 'medium') return s
  return 'low'
}

/**
 * 风险预警页仪表盘数据：
 * - 地图点位/等级统计：复用国别管理“分析风险”的聚合方式（按当前全量国别风险等级聚合）
 * - 最新更新：复用“预警发布”列表（取最近若干条，点击可触发发布）
 */
export async function fetchRiskWarningDashboard(): Promise<RiskWarningDashboard> {
  const [countries, recent] = await Promise.all([
    fetchAllCountryRecords({
      keyword: undefined,
      continent: undefined,
      riskLevel: undefined,
      sortBy: 'updatedAtDesc',
    }),
    http.get<SpringPage<CountryRiskChangeRecentItem>>(
      '/biz/overseas/globalcountry/country/riskChange/page',
      {
        params: { current: 1, size: 5000 },
        skipErrorMessage: true,
      },
    ),
  ])

  const mapPoints: RiskMapPoint[] = []
  const cnt: Record<RiskLevel, number> = { extreme: 0, high: 0, medium: 0, low: 0 }
  const countriesByLevel: CountriesByRiskLevel = { extreme: [], high: [], medium: [], low: [] }

  for (const c of countries) {
    const level = normalizeRiskLevel(c.riskLevel)
    cnt[level] += 1
    const cnName = String(c.countryName ?? '').trim()
    if (cnName) {
      countriesByLevel[level].push(cnName)
    }

    const lng = c.longitude
    const lat = c.latitude
    if (lng != null && lat != null && Number.isFinite(lng) && Number.isFinite(lat)) {
      mapPoints.push({
        id: c.id || c.countryCode || c.countryName,
        country: c.countryName,
        city: '',
        level,
        lng,
        lat,
      })
    }
  }

  const levelStats: RiskLevelStat[] = (['extreme', 'high', 'medium', 'low'] as const).map(
    (level) => ({
      level,
      label: LEVEL_LABEL[level],
      count: cnt[level] ?? 0,
      unit: '个国家',
    }),
  )

  for (const k of ['extreme', 'high', 'medium', 'low'] as const) {
    countriesByLevel[k].sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'))
  }

  const updatesRaw = Array.isArray(recent?.records) ? recent.records : []
  const updates: RiskUpdateRecord[] = updatesRaw.map((x) => {
    const toLevel = normalizeRiskLevel(x.toRiskLevel)
    const fromMissing = !x.fromRiskLevel || String(x.fromRiskLevel).trim() === ''
    // fromRiskLevel 为空表示“首次配置/无历史值”，level 仍用 toLevel 兜底以保证渲染稳定
    const fromLevel = !fromMissing ? normalizeRiskLevel(x.fromRiskLevel) : toLevel
    const timeText = x.operateTime ? String(x.operateTime).slice(0, 19).replace('T', ' ') : ''
    return {
      id: String(x.geonameid ?? ''),
      country: String(x.countryName ?? x.countryCode ?? '—'),
      timeText,
      fromLevel,
      toLevel,
      fromMissing,
    }
  })

  return { mapPoints, levelStats, updates, countriesByLevel }
}

/**
 * 发布：复用“预警发布”的发布动作
 */
export async function publishRiskWarning(payload: PublishRiskPayload): Promise<void> {
  await publishWarningRelease(payload.id)
}

export function riskLevelLabel(level: RiskLevel): string {
  return LEVEL_LABEL[level]
}
